import { useState, useEffect } from "react";
import axiosInstance from '../../api/axios';
import { Link } from "react-router-dom";

const formatTime = (t) => {
  if (!t) return null;
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 || 12;
  return `${display}:${m} ${ampm}`;
};

const SlotCell = ({ row }) => {
  if (!row) return <td style={styles.td}><span style={styles.empty}>—</span></td>;

  const slots = [
    { label: "Morning",   time: formatTime(row.morningSlot),   count: row.morningCount },
    { label: "Afternoon", time: formatTime(row.afternoonSlot), count: row.afternoonCount },
    { label: "Evening",   time: formatTime(row.eveningSlot),   count: row.eveningCount },
  ].filter(s => s.time);

  if (!slots.length) return <td style={styles.td}><span style={styles.empty}>—</span></td>;

  return (
    <td style={styles.td}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {slots.map((s, i) => (
          <div key={i} style={styles.slotChip}>
            <span style={styles.slotLabel}>{s.label}</span>
            <span style={styles.slotTime}>{s.time}</span>
            <span style={styles.slotCount}>
              {s.count ? `${s.count} student${s.count === "1" ? "" : "s"}` : "-"}
            </span>
          </div>
        ))}
      </div>
    </td>
  );
};

const COMMENT_PREVIEW_LEN = 80;

const CommentCell = ({ comments }) => {
  const [expanded, setExpanded] = useState(false);
  if (!comments) return null;

  const isLong = comments.length > COMMENT_PREVIEW_LEN;
  const preview = isLong ? comments.slice(0, COMMENT_PREVIEW_LEN) + "…" : comments;

  return (
    <div style={{ marginTop: 5, fontWeight: 400 }}>
      <div style={styles.commentText}>
        &#128172; {expanded || !isLong ? comments : preview}
      </div>
      {isLong && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={styles.expandBtn}
        >
          {expanded ? "Collapse ▲" : "Expand ▼"}
        </button>
      )}
    </div>
  );
};

export const AllVivaSlots = () => {
  const [byExaminer, setByExaminer] = useState({});
  const [allDates, setAllDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // const navigate = useNavigate();

  useEffect(() => {
    axiosInstance.get("/getAllSlots")
      .then(res => {
        const rows = res.data.data || [];

        const grouped = {};
        const dateSet = new Set();

        rows.forEach(row => {
          if (!row.morningSlot && !row.afternoonSlot && !row.eveningSlot) return;
          const key = row.examinerId;
          const date = row.displayDate || row.date;
          dateSet.add(date);
          if (!grouped[key]) {
            grouped[key] = {
              name: row.examinerName || `Examiner ${key}`,
              preferredSlotCount: row.preferredSlotCount ?? null,
              comments: row.comments || null,
              byDate: {}
            };
          }
          grouped[key].byDate[date] = row;
        });

        const sorted = [...dateSet].sort((a, b) => {
          const parse = d => {
            const [mm, dd, yy] = d.split("/");
            return new Date(`20${yy}-${mm}-${dd}`);
          };
          return parse(a) - parse(b);
        });

        setByExaminer(grouped);
        setAllDates(sorted);
      })
      .catch(err => {
        console.error(err);
        setError("Failed to load slots.");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (error) return <div style={{ padding: 24, color: "red" }}>{error}</div>;

  const entries = Object.entries(byExaminer);

  // if (!entries.length || !allDates.length) {
  //   return <div style={{ padding: 24, color: "#666" }}>No slots have been filled yet.</div>;
  // }

  const styles = {
    configButton: {
      background: "#1976d2",
      color: "#fff",
      border: "none",
      padding: "10px 18px",
      borderRadius: 6,
      cursor: "pointer",
      fontWeight: 600,
      fontSize: 14,
    },
  };


  return (
    <div style={{ padding: 24 }}>
      <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20
      }}
    >
      <div>
        <h2 style={{ marginBottom: 6, fontSize: 20 }}>Viva Slot Availability</h2>
        <p style={{ color: "#666", fontSize: 13 }}>
          Showing only dates where examiners have selected at least one slot.
        </p>
      </div>

      <Link to="/vivaConfiguration/" className="link" >
                Configure Viva Masters
                </Link>
    </div>

      <div style={{ overflowX: "auto" }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, ...styles.examinerTh }}>Examiner</th>
              {allDates.map(date => (
                <th key={date} style={styles.th}>{date}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map(([examinerId, { name, preferredSlotCount, comments, byDate }]) => (
              <tr key={examinerId}>
                <td style={{ ...styles.td, ...styles.examinerTd }}>
                  <div>{name}</div>
                  {preferredSlotCount != null && (
                    <div style={styles.prefBadge}>
                      Prefers at most {preferredSlotCount} slot{preferredSlotCount !== 1 ? "s" : ""}
                    </div>
                  )}
                  <CommentCell comments={comments} />
                </td>
                {allDates.map(date => (
                  <SlotCell key={date} row={byDate[date] || null} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  table: {
    borderCollapse: "collapse",
    width: "100%",
    background: "#fff",
    border: "1px solid #dee2e6",
    borderRadius: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    fontSize: 13,
  },
  th: {
    padding: "10px 14px",
    background: "#f8f9fa",
    borderBottom: "2px solid #dee2e6",
    borderRight: "1px solid #dee2e6",
    fontWeight: 700,
    color: "#333",
    whiteSpace: "nowrap",
    textAlign: "center",
  },
  examinerTh: {
    textAlign: "left",
    minWidth: 160,
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: "#f8f9fa",
  },
  td: {
    padding: "10px 14px",
    borderBottom: "1px solid #f0f0f0",
    borderRight: "1px solid #f0f0f0",
    verticalAlign: "top",
    textAlign: "center",
  },
  examinerTd: {
    fontWeight: 600,
    color: "#1a1a1a",
    textAlign: "left",
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    background: "#fff",
    zIndex: 1,
    borderRight: "2px solid #dee2e6",
  },
  slotChip: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    background: "#e8f5e9",
    border: "1px solid #a5d6a7",
    borderRadius: 6,
    padding: "4px 10px",
  },
  slotLabel: {
    fontSize: 10,
    color: "#777",
  },
  slotTime: {
    fontWeight: 700,
    color: "#1b5e20",
    fontSize: 13,
  },
  slotCount: {
    fontSize: 10,
    color: "#388e3c",
  },
  empty: {
    color: "#ccc",
    fontSize: 16,
  },
  prefBadge: {
    marginTop: 4,
    display: "inline-block",
    fontSize: 11,
    fontWeight: 600,
    background: "#e3f2fd",
    color: "#1565c0",
    border: "1px solid #90caf9",
    borderRadius: 4,
    padding: "1px 6px",
  },
  commentText: {
    fontSize: 11,
    color: "#555",
    whiteSpace: "normal",
    maxWidth: 200,
    lineHeight: "1.5",
    wordBreak: "break-word",
  },
  expandBtn: {
    marginTop: 4,
    padding: "2px 8px",
    fontSize: 11,
    color: "#1565c0",
    background: "transparent",
    border: "1px solid #90caf9",
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: 600,
  },
};

export default AllVivaSlots;
